import React, { useRef } from 'react';
import styled from 'styled-components';
import { C, EASE_OUT, ROADMAP, hover } from '../kit';
import { MILESTONE_ART, yearOf } from '../roadmapArt';
import { css } from 'styled-components';

/**
 * The roadmap on desktop as one rail that fits a screen: every milestone side by side with
 * its picture and its year, a dashed trail running through all of them, arrows to walk it.
 * It replaces the tall vertical path, which took a screen and a half.
 */
export const RoadmapRail: React.FC = () => {
  const railRef = useRef<HTMLOListElement>(null);
  const walk = (dir: 1 | -1) => {
    const rail = railRef.current;
    if (rail && typeof rail.scrollBy === 'function') rail.scrollBy({ left: dir * rail.clientWidth * 0.75, behavior: 'smooth' });
  };
  return (
    <Wrap>
      <Arrow type="button" aria-label="Earlier milestones" className="prev" onClick={() => walk(-1)}>‹</Arrow>
      <Rail ref={railRef} data-rail>
        {ROADMAP.map((stop, i) => (
          <Stop key={stop.milestone.id} data-stop $first={i === 0} $last={i === ROADMAP.length - 1} $done={stop.milestone.status !== 'planned'}>
            <div className="art">
              <img data-milestone-art src={MILESTONE_ART[i % MILESTONE_ART.length]} alt="" loading="lazy" style={{ transform: `rotate(${i % 2 ? 2 : -2}deg)` }} />
            </div>
            <span className="trail" aria-hidden="true" />
            <div className="what">
              {stop.pill && <span className="pill">{stop.pill}</span>}
              <h3>{stop.lines.join(' ')}</h3>
              <p>{stop.milestone.description}</p>
              {stop.milestone.link && (
                <a href={stop.milestone.link.href} target="_blank" rel="noopener noreferrer">
                  {stop.milestone.link.label} ↗
                </a>
              )}
            </div>
            <span className="year" data-year aria-hidden="true">{yearOf(stop)}</span>
          </Stop>
        ))}
      </Rail>
      <Arrow type="button" aria-label="Later milestones" className="next" onClick={() => walk(1)}>›</Arrow>
    </Wrap>
  );
};

const Wrap = styled.div`
  position: relative;
  max-width: 1320px;
  margin: 0 auto;
`;

const Rail = styled.ol`
  display: flex;
  margin: 0;
  padding: 0 clamp(40px, 5vw, 72px);
  list-style: none;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding: 0 clamp(40px, 5vw, 72px);
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Stop = styled.li<{ $first: boolean; $last: boolean; $done: boolean }>`
  position: relative;
  flex: none;
  width: clamp(260px, 24vw, 330px);
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  .year {
    margin: auto 18px 0;
    padding-top: 14px;
    font-family: 'GROBOLD', sans-serif;
    font-size: clamp(36px, 6vh, 64px);
    line-height: 1;
    color: ${C.yellow};
    -webkit-text-stroke: 3px ${C.ink};
    paint-order: stroke fill;
    text-shadow: 4px 5px 0 ${C.ink};
    transform: rotate(-4deg);
    transform-origin: left bottom;
    pointer-events: none;
  }
  .art {
    position: relative;
    height: clamp(130px, 23vh, 270px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 18px;
  }
  .art img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    border: 4px solid ${C.ink};
    border-radius: 6px;
    background: ${C.paper};
    box-shadow: 5px 6px 0 ${C.ink};
    transition: transform 400ms ${EASE_OUT};
  }
  ${hover(css`
    &:hover .art img { transform: rotate(0deg) scale(1.05) !important; }
  `)}
  .trail {
    position: relative;
    height: 30px;
    flex: none;
  }
  .trail::before {
    content: '';
    position: absolute;
    top: 50%;
    left: ${(p) => (p.$first ? '50%' : '0')};
    right: ${(p) => (p.$last ? '50%' : '0')};
    border-top: 4px dashed ${C.ink};
    transform: translateY(-50%);
  }
  .trail::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 4px solid ${C.ink};
    background: ${(p) => (p.$done ? C.yellow : C.white)};
    box-shadow: 2px 3px 0 ${C.ink};
    transform: translate(-50%, -50%);
  }
  .what {
    padding: 4px 18px 0;
    color: ${C.ink};
  }
  .pill {
    display: inline-block;
    margin-bottom: 8px;
    padding: 3px 12px;
    border: 3px solid ${C.ink};
    border-radius: 999px;
    background: ${C.yellow};
    font-family: 'Ubuntu', sans-serif;
    font-weight: 700;
    font-size: 14px;
    box-shadow: 2px 3px 0 ${C.ink};
  }
  h3 {
    margin: 0 0 8px;
    font-family: 'GROBOLD', sans-serif;
    font-weight: 400;
    font-size: clamp(19px, 2.4vh, 23px);
    line-height: 1.15;
  }
  p {
    margin: 0;
    font-family: 'Ubuntu', sans-serif;
    font-weight: 500;
    font-size: clamp(13px, 1.6vh, 15px);
    line-height: 1.45;
  }
  a {
    display: inline-block;
    margin-top: 8px;
    color: ${C.ink};
    font-family: 'GROBOLD', sans-serif;
    font-size: 16px;
  }
`;

const Arrow = styled.button`
  position: absolute;
  top: calc(clamp(130px, 23vh, 270px) + 15px);
  z-index: 2;
  width: 52px;
  height: 52px;
  transform: translateY(-50%);
  border: 4px solid ${C.ink};
  border-radius: 50%;
  background: ${C.yellow};
  color: ${C.ink};
  font-family: 'GROBOLD', sans-serif;
  font-size: 32px;
  line-height: 1;
  box-shadow: 3px 4px 0 ${C.ink};
  cursor: pointer;
  transition: transform 200ms ${EASE_OUT};
  &.prev { left: 0; }
  &.next { right: 0; }
  ${hover(css`
    &:hover { transform: translateY(-50%) scale(1.08); }
  `)}
  &:active { transform: translateY(-50%) scale(0.94); }
`;
